import { useEffect, useMemo, useRef, useState } from 'react';

import {
    CHAT_USER_EXPIRY_SWEEP_INTERVAL_MS,
    pruneExpiredChatUsers,
} from '@apps/web/hooks/chat_expiring_users';
import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { getApiClient } from '@apps/web/lib/api_client';
import { type ApiResponseType } from '@libraries/typescript/api_client';

const CHAT_PRESENCE_PATH =
    '/organizations/{organization_id}/runs/{run_id}/presence';
const CHAT_VIEWER_HEARTBEAT_INTERVAL_MS = 15_000;
const CHAT_VIEWER_EXPIRY_MS = 45_000;

type AgentRunPresenceResponse = ApiResponseType<
    typeof CHAT_PRESENCE_PATH,
    'post'
>;

export type ChatViewer = {
    readonly userId: string;
    readonly displayName: string;
};

type ViewerEntry = ChatViewer & {
    readonly expiresAt: number;
};

type ViewerState = {
    readonly scopeKey: string | null;
    readonly byUserId: Record<string, ViewerEntry>;
};

type ViewerDelta = {
    readonly viewer: ChatViewer;
    readonly viewing: boolean;
    readonly revision: number;
};

function viewerScopeKey(organizationId: string, runId: string): string {
    return `${organizationId}:${runId}`;
}

function responseViewersByUserId(
    response: AgentRunPresenceResponse,
): Record<string, ViewerEntry> {
    const expiresAt = Date.now() + CHAT_VIEWER_EXPIRY_MS;
    return Object.fromEntries(
        response.viewers.map((viewer) => [
            viewer.user_id,
            {
                userId: viewer.user_id,
                displayName: viewer.display_name,
                expiresAt,
            },
        ]),
    );
}

export function usePhoebeChatPresence({
    organizationId,
    runId,
    currentUserId,
    enabled,
    visible,
}: {
    readonly organizationId: string | null;
    readonly runId: string | null;
    readonly currentUserId: string | null;
    readonly enabled: boolean;
    readonly visible: boolean;
}): { readonly viewers: ReadonlyArray<ChatViewer> } {
    const connectionIdRef = useRef(crypto.randomUUID());
    const requestChainRef = useRef<Promise<void>>(Promise.resolve());
    const snapshotRevisionRef = useRef(0);
    const viewerDeltasRef = useRef<Record<string, ViewerDelta>>({});
    const [documentVisible, setDocumentVisible] = useState(
        () => document.visibilityState !== 'hidden',
    );
    const [viewerState, setViewerState] = useState<ViewerState>({
        scopeKey: null,
        byUserId: {},
    });
    const scopeKey =
        enabled && organizationId && runId && currentUserId
            ? viewerScopeKey(organizationId, runId)
            : null;

    useEffect(() => {
        const handleVisibilityChange = () => {
            setDocumentVisible(document.visibilityState !== 'hidden');
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () =>
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            );
    }, []);

    useEffect(() => {
        snapshotRevisionRef.current = 0;
        viewerDeltasRef.current = {};
        if (
            !scopeKey ||
            !organizationId ||
            !runId ||
            !visible ||
            !documentVisible
        ) {
            setViewerState({ scopeKey: null, byUserId: {} });
            return;
        }
        setViewerState({ scopeKey, byUserId: {} });
    }, [documentVisible, organizationId, runId, scopeKey, visible]);

    useListenForOrganizationEvent(
        'chat_presence',
        (event) => {
            if (
                event.type !== 'chat_presence' ||
                scopeKey === null ||
                event.data.run_id !== runId
            ) {
                return;
            }
            if (event.data.revision <= snapshotRevisionRef.current) {
                return;
            }
            const previousDelta = viewerDeltasRef.current[event.data.user_id];
            if (
                previousDelta !== undefined &&
                event.data.revision < previousDelta.revision
            ) {
                return;
            }
            viewerDeltasRef.current = {
                ...viewerDeltasRef.current,
                [event.data.user_id]: {
                    viewer: {
                        userId: event.data.user_id,
                        displayName: event.data.display_name,
                    },
                    viewing: event.data.viewing,
                    revision: event.data.revision,
                },
            };
            setViewerState((previous) => {
                const next =
                    previous.scopeKey === scopeKey
                        ? { ...previous.byUserId }
                        : {};
                if (event.data.viewing) {
                    next[event.data.user_id] = {
                        userId: event.data.user_id,
                        displayName: event.data.display_name,
                        expiresAt: Date.now() + CHAT_VIEWER_EXPIRY_MS,
                    };
                } else {
                    delete next[event.data.user_id];
                }
                return {
                    scopeKey,
                    byUserId: next,
                };
            });
        },
        {
            enabled: scopeKey !== null && visible && documentVisible,
            organizationId,
        },
    );

    useEffect(() => {
        if (
            scopeKey === null ||
            !organizationId ||
            !runId ||
            !visible ||
            !documentVisible
        ) {
            return;
        }

        let active = true;
        const connectionId = connectionIdRef.current;
        const queuePresence = (viewing: boolean) => {
            requestChainRef.current = requestChainRef.current
                .catch(() => undefined)
                .then(async () => {
                    const response = await getApiClient().post({
                        url: CHAT_PRESENCE_PATH,
                        pathParams: {
                            organization_id: organizationId,
                            run_id: runId,
                        },
                        body: {
                            connection_id: connectionId,
                            viewing,
                        },
                        bestEffort: true,
                    });
                    if (active && viewing) {
                        if (response.revision < snapshotRevisionRef.current) {
                            return;
                        }
                        const next = responseViewersByUserId(response);
                        for (const delta of Object.values(
                            viewerDeltasRef.current,
                        )) {
                            if (delta.revision <= response.revision) {
                                continue;
                            }
                            if (delta.viewing) {
                                next[delta.viewer.userId] = {
                                    ...delta.viewer,
                                    expiresAt:
                                        Date.now() + CHAT_VIEWER_EXPIRY_MS,
                                };
                            } else {
                                delete next[delta.viewer.userId];
                            }
                        }
                        snapshotRevisionRef.current = response.revision;
                        viewerDeltasRef.current = Object.fromEntries(
                            Object.entries(viewerDeltasRef.current).filter(
                                ([, delta]) =>
                                    delta.revision > response.revision,
                            ),
                        );
                        setViewerState({
                            scopeKey,
                            byUserId: next,
                        });
                    }
                })
                // Presence is best-effort. The next heartbeat retries, and
                // both Redis and the browser sweep expire a missed leave.
                .catch(() => undefined);
        };

        queuePresence(true);
        const heartbeatInterval = window.setInterval(
            () => queuePresence(true),
            CHAT_VIEWER_HEARTBEAT_INTERVAL_MS,
        );
        return () => {
            active = false;
            window.clearInterval(heartbeatInterval);
            // Queue the leave behind an in-flight heartbeat so a slow join can
            // never resurrect this tab after it switches chats or hides.
            queuePresence(false);
        };
    }, [documentVisible, organizationId, runId, scopeKey, visible]);

    const scopedEntries = useMemo(
        () => (viewerState.scopeKey === scopeKey ? viewerState.byUserId : {}),
        [scopeKey, viewerState],
    );
    const hasViewers = Object.keys(scopedEntries).length > 0;
    useEffect(() => {
        if (!hasViewers || scopeKey === null) {
            return;
        }
        const intervalId = window.setInterval(() => {
            setViewerState((previous) => {
                if (previous.scopeKey !== scopeKey) {
                    return previous;
                }
                const remaining = pruneExpiredChatUsers(
                    previous.byUserId,
                    Date.now(),
                );
                return remaining === previous.byUserId
                    ? previous
                    : {
                          scopeKey,
                          byUserId: remaining,
                      };
            });
        }, CHAT_USER_EXPIRY_SWEEP_INTERVAL_MS);
        return () => window.clearInterval(intervalId);
    }, [hasViewers, scopeKey]);

    const viewers = useMemo(
        () =>
            Object.values(scopedEntries)
                .sort((left, right) => {
                    if (left.userId === currentUserId) {
                        return -1;
                    }
                    if (right.userId === currentUserId) {
                        return 1;
                    }
                    return (
                        left.displayName.localeCompare(right.displayName) ||
                        left.userId.localeCompare(right.userId)
                    );
                })
                .map(({ userId, displayName }) => ({
                    userId,
                    displayName,
                })),
        [currentUserId, scopedEntries],
    );

    return { viewers };
}
